import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Paginated, Resource } from '@/lib/api/types'

export interface EmailAutomation {
  id: string
  key: string | null
  name: string
  trigger: string
  delay_minutes: number
  condition: string | null
  subject: string
  preheader: string | null
  body: string
  button_label: string | null
  button_url: string | null
  enabled: boolean
  built_in: boolean
  updated_at: string | null
  updated_by: string | null
}

export interface EmailSend {
  id: number
  email: string | null
  to: string | null
  status: 'scheduled' | 'sent' | 'skipped' | 'failed'
  reason: string | null
  send_at: string
  sent_at: string | null
}

export interface EmailList {
  data: (EmailAutomation & {
    last_30_days: Record<'sent' | 'scheduled' | 'skipped' | 'failed', number>
    /** The step it prompts, and how many it went to in the last 30 days took it afterwards. */
    next_step?: { label: string; sent: number; did: number } | null
  })[]
  meta: {
    triggers: Record<string, { label: string; description: string }>
    conditions: Record<string, string>
    merge_tags: Record<string, string>
    can_edit: boolean
  }
}

export type EmailInput = Pick<
  EmailAutomation,
  | 'name'
  | 'trigger'
  | 'delay_minutes'
  | 'condition'
  | 'subject'
  | 'preheader'
  | 'body'
  | 'button_label'
  | 'button_url'
> & { enabled?: boolean }

export type EmailContent = Pick<EmailInput, 'subject' | 'preheader' | 'body' | 'button_label' | 'button_url'>

export function useEmails() {
  return useQuery({ queryKey: ['emails'], queryFn: () => http.get<EmailList>('/emails') })
}

export type SendStatusFilter = EmailSend['status'] | 'all'

/** Sends of every lifecycle email, newest first, 25 a page. */
export function useEmailSends(status: SendStatusFilter, email: string, search: string, page: number) {
  return useQuery({
    queryKey: ['emails', 'sends', status, email, search, page],
    queryFn: () =>
      http.get<Paginated<EmailSend>>('/emails/sends', {
        status: status === 'all' ? undefined : status,
        email,
        search,
        page,
      }),
    placeholderData: keepPreviousData,
  })
}

export function useEmail(id: string | undefined) {
  return useQuery({
    queryKey: ['emails', id],
    enabled: id !== undefined,
    queryFn: async () => (await http.get<Resource<EmailAutomation>>(`/emails/${id ?? ''}`)).data,
  })
}

export function useSaveEmail(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<EmailInput>) =>
      id === undefined
        ? (await http.post<Resource<EmailAutomation>>('/emails', input)).data
        : (await http.patch<Resource<EmailAutomation>>(`/emails/${id}`, input)).data,
    onSuccess: (email) => {
      queryClient.setQueryData(['emails', email.id], email)
      void queryClient.invalidateQueries({ queryKey: ['emails'], exact: true })
    },
  })
}

export function useDeleteEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/emails/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['emails'] }),
  })
}

/** The email as it will look, with made-up details (rendered by the API, so it's exact). */
export function useEmailPreview(content: EmailContent, enabled: boolean) {
  return useQuery({
    queryKey: ['emails', 'preview', content],
    enabled: enabled && content.subject.trim() !== '' && content.body.trim() !== '',
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await http.post<Resource<{ subject: string; html: string }>>('/emails/preview', content)).data,
  })
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (content: EmailContent) =>
      (await http.post<Resource<{ sent_to: string }>>('/emails/test', content)).data,
  })
}

/** "Straight away", "After 2 days", "After 3 hours"… */
export function waitLabel(minutes: number): string {
  if (minutes === 0) return 'Straight away'
  if (minutes % 1440 === 0) return `After ${minutes / 1440} ${minutes === 1440 ? 'day' : 'days'}`
  if (minutes % 60 === 0) return `After ${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`
  return `After ${minutes} minutes`
}
